import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import classNames from 'classnames';
import Button from '../../bootstrap/Button';
import FormGroup from '../../bootstrap/forms/FormGroup';
import Input from '../../bootstrap/forms/Input';
import Select from '../../bootstrap/forms/Select';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../bootstrap/Modal';
import BtnLoad from '../../MyCustom/BtnLoad';
import { DefaultContext } from '../../../contexts/default';
import FiscalGroupDB from '../../../database/wrappers/fiscalGroup';
import PreAlert from '../../../helpers/utils/preAlert';
import { TRIBUTARY_SITUATION } from '../../../types/fiscal';
import {
  ALIQ_ICMS_OPTIONS_BASE,
  CSOSN_OPTIONS_BASE,
  CST_CBS_IBS_OPTIONS_BASE,
  CST_OPTIONS_BASE,
  PERCENTUAL_REDUCAO_OPTIONS_BASE,
  buildCClassTribDatalistOptions,
  buildCestDatalistOptions,
  buildCfopDatalistOptions,
  buildLimitedDatalistOptions,
  buildOptionsWithCurrent,
  validateFiscalGroupForm,
} from '../../../utils/fiscal/fiscalGroupModalValues';

const ModalRegisterFiscalGroups = ({ open, setIsOpen, setIsClose, fiscalGroupSelected }) => {
  const { establishment, onShowAlert } = useContext(DefaultContext);
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: '',
      fiscal_data_cst: '',
      fiscal_data_csosn: '',
      fiscal_data_cest: '',
      fiscal_data_cfop: '',
      fiscal_data_aliq_icms: '',
      fiscal_data_tributary_situation: '',
      fiscal_data_pis: '',
      fiscal_data_cofins: '',
      fiscal_data_cBenef: '',
      fiscal_data_cClassTrib: '',
      fiscal_data_cst_cbs_ibs: '',
      fiscal_data_percentualReducao: '',
      fiscal_data_pRedICMS: '',
    },
    validate: validateFiscalGroupForm,
    onSubmit: values => {
      const data = {
        name: values.name,
        active: true,
        fiscal_data: {
          cst: values.fiscal_data_cst ?? '',
          csosn: values.fiscal_data_csosn ?? '',
          cest: values.fiscal_data_cest ?? '',
          cfop: values.fiscal_data_cfop ?? '',
          aliq_icms: (values.fiscal_data_aliq_icms || '').replace(',', '.'),
          tributary_situation: values.fiscal_data_tributary_situation,
          pis: values.fiscal_data_pis ?? '',
          cofins: values.fiscal_data_cofins ?? '',
          cBenef: values.fiscal_data_cBenef ?? '',
          cClassTrib: values.fiscal_data_cClassTrib ?? '',
          cst_cbs_ibs: values.fiscal_data_cst_cbs_ibs ?? '',
          percentualReducao: values.fiscal_data_percentualReducao ?? '',
          pRedICMS: String(values.fiscal_data_pRedICMS ?? '').replace(',', '.'),
        },
      };

      const onSuccess = () => {
        onShowAlert(PreAlert.success('Registro cadastrado com sucesso'));
        setIsClose();
      };
      const onSuccessUpdate = () => {
        onShowAlert(PreAlert.success('Registro atualizado com sucesso'));
        setIsClose();
      };
      const onError = () => onShowAlert(PreAlert.error('Falhou ao cadastrar o registro'));

      setIsLoading(true);
      if (fiscalGroupSelected) {
        new FiscalGroupDB(establishment.id)
          .update(fiscalGroupSelected.id, data)
          .then(onSuccessUpdate)
          .catch(onError)
          .finally(() => setIsLoading(false));
      } else {
        new FiscalGroupDB(establishment.id)
          .create(data)
          .then(onSuccess)
          .catch(onError)
          .finally(() => setIsLoading(false));
      }
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      return;
    }

    if (!fiscalGroupSelected) return;
    const { name, fiscal_data } = fiscalGroupSelected;
    formik.setValues({
      name: name || '',
      fiscal_data_cst: fiscal_data?.cst || '',
      fiscal_data_csosn: fiscal_data?.csosn || '',
      fiscal_data_cest: fiscal_data?.cest || '',
      fiscal_data_cfop: fiscal_data?.cfop || '',
      fiscal_data_aliq_icms: fiscal_data?.aliq_icms || '',
      fiscal_data_tributary_situation: String(fiscal_data?.tributary_situation ?? ''),
      fiscal_data_pis: fiscal_data?.pis || '',
      fiscal_data_cofins: fiscal_data?.cofins || '',
      fiscal_data_cBenef: fiscal_data?.cBenef || '',
      fiscal_data_cClassTrib: fiscal_data?.cClassTrib || '',
      fiscal_data_cst_cbs_ibs: fiscal_data?.cst_cbs_ibs || '',
      fiscal_data_percentualReducao: fiscal_data?.percentualReducao || '',
      fiscal_data_pRedICMS: fiscal_data?.pRedICMS || '',
    });
  }, [open, fiscalGroupSelected]);

  const tributaryOptions = useMemo(() => ([
    { value: '', text: 'Selecione uma opção' },
    { value: String(TRIBUTARY_SITUATION.ISENTO), text: 'Isento' },
    { value: String(TRIBUTARY_SITUATION.TRIBUTO_18), text: 'Tributado' },
    { value: String(TRIBUTARY_SITUATION.SUBS_TRIBUTARIO), text: 'Substituição Tributária' },
  ]), []);

  const cstOptions = useMemo(
    () => buildOptionsWithCurrent(CST_OPTIONS_BASE, formik.values.fiscal_data_cst),
    [formik.values.fiscal_data_cst],
  );
  const csosnOptions = useMemo(
    () => buildOptionsWithCurrent(CSOSN_OPTIONS_BASE, formik.values.fiscal_data_csosn),
    [formik.values.fiscal_data_csosn],
  );
  const aliqIcmsOptions = useMemo(
    () => buildOptionsWithCurrent(ALIQ_ICMS_OPTIONS_BASE, formik.values.fiscal_data_aliq_icms),
    [formik.values.fiscal_data_aliq_icms],
  );
  const percentualReducaoOptions = useMemo(
    () => buildOptionsWithCurrent(PERCENTUAL_REDUCAO_OPTIONS_BASE, formik.values.fiscal_data_percentualReducao),
    [formik.values.fiscal_data_percentualReducao],
  );
  const cstCbsIbsOptions = useMemo(
    () => buildOptionsWithCurrent(CST_CBS_IBS_OPTIONS_BASE, formik.values.fiscal_data_cst_cbs_ibs),
    [formik.values.fiscal_data_cst_cbs_ibs],
  );

  const cestDatalistOptions = useMemo(() => {
    const entries = buildCestDatalistOptions(formik.values.fiscal_data_cest);
    return buildLimitedDatalistOptions(entries, formik.values.fiscal_data_cest, { minChars: 2, limit: 80 });
  }, [formik.values.fiscal_data_cest]);
  const cfopDatalistOptions = useMemo(() => {
    const entries = buildCfopDatalistOptions(formik.values.fiscal_data_cfop);
    return buildLimitedDatalistOptions(entries, formik.values.fiscal_data_cfop, { minChars: 2, limit: 80 });
  }, [formik.values.fiscal_data_cfop]);
  const cClassTribDatalistOptions = useMemo(() => {
    const entries = buildCClassTribDatalistOptions(formik.values.fiscal_data_cClassTrib);
    return buildLimitedDatalistOptions(entries, formik.values.fiscal_data_cClassTrib, { minChars: 2, limit: 80 });
  }, [formik.values.fiscal_data_cClassTrib]);

  return (
    <Modal
      id='modal-register-fiscal-group'
      titleId='Cadastro de Grupo Fiscal'
      isOpen={open}
      setIsOpen={setIsOpen}
      isStaticBackdrop
      isScrollable
      isCentered
      size='lg'
      isAnimation
      onSubmit={formik.handleSubmit}>
      <ModalHeader setIsOpen={setIsOpen}>
        <ModalTitle id='register-fiscal-group'>
          {fiscalGroupSelected ? 'Atualização de Grupo Fiscal' : 'Cadastro de Grupo Fiscal'}
        </ModalTitle>
      </ModalHeader>

      <ModalBody className='p-5'>
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className='row pb-2 d-flex'>
            <FormGroup id='name' label='Nome do Grupo' className='col-md-12 mb-4'>
              <Input
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                isValid={formik.isValid}
                isTouched={formik.touched.name}
                invalidFeedback={formik.errors.name}
                validFeedback='Assim está bom!'
                placeholder='Ex.: Tributação Padrão Restaurante'
              />
            </FormGroup>

            <FormGroup id='fiscal_data_cst' label='CST / ICMS' className='col-md-4 mb-4'>
              <Select
                list={cstOptions}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_cst}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_cst}
                invalidFeedback={formik.errors.fiscal_data_cst}
              />
            </FormGroup>

            <FormGroup id='fiscal_data_csosn' label='CSOSN' className='col-md-4 mb-4'>
              <Select
                list={csosnOptions}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_csosn}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_csosn}
                invalidFeedback={formik.errors.fiscal_data_csosn}
              />
            </FormGroup>

            <FormGroup id='fiscal_data_cest' label='CEST' className='col-md-4 mb-4'>
              <input
                name='fiscal_data_cest'
                list='fiscal_data_cest-list'
                className={classNames('form-control', {
                  'is-invalid': !formik.isValid && formik.touched.fiscal_data_cest && formik.errors.fiscal_data_cest,
                  'is-valid': !formik.isValid && formik.touched.fiscal_data_cest && !formik.errors.fiscal_data_cest,
                })}
                value={formik.values.fiscal_data_cest}
                onChange={(e) => formik.setFieldValue('fiscal_data_cest', e.target.value.replace(/\D/g, ''))}
                onBlur={formik.handleBlur}
                placeholder='Digite 2+ dígitos'
              />
            </FormGroup>
            <datalist id='fiscal_data_cest-list'>
              {cestDatalistOptions.map(item => (
                <option key={item.code} value={item.code} label={`${item.code} - ${item.text}`} />
              ))}
            </datalist>

            <FormGroup id='fiscal_data_cfop' label='CFOP' className='col-md-4 mb-4'>
              <input
                name='fiscal_data_cfop'
                list='fiscal_data_cfop-list'
                className={classNames('form-control', {
                  'is-invalid': !formik.isValid && formik.touched.fiscal_data_cfop && formik.errors.fiscal_data_cfop,
                  'is-valid': !formik.isValid && formik.touched.fiscal_data_cfop && !formik.errors.fiscal_data_cfop,
                })}
                value={formik.values.fiscal_data_cfop}
                onChange={(e) => formik.setFieldValue('fiscal_data_cfop', e.target.value.replace(/\D/g, ''))}
                onBlur={formik.handleBlur}
                placeholder='Digite 2+ dígitos'
              />
            </FormGroup>
            <datalist id='fiscal_data_cfop-list'>
              {cfopDatalistOptions.map(item => (
                <option key={item.code} value={item.code} label={`${item.code} - ${item.text}`} />
              ))}
            </datalist>

            <FormGroup id='fiscal_data_aliq_icms' label='Alíquota ICMS' className='col-md-4 mb-4'>
              <Select
                list={aliqIcmsOptions}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_aliq_icms}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_aliq_icms}
                invalidFeedback={formik.errors.fiscal_data_aliq_icms}
              />
            </FormGroup>

            <FormGroup id='fiscal_data_pis' label='PIS' className='col-md-3 mb-4'>
              <Input
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_pis}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_pis}
                invalidFeedback={formik.errors.fiscal_data_pis}
              />
            </FormGroup>

            <FormGroup id='fiscal_data_cofins' label='COFINS' className='col-md-3 mb-4'>
              <Input
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_cofins}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_cofins}
                invalidFeedback={formik.errors.fiscal_data_cofins}
              />
            </FormGroup>

            <FormGroup id='fiscal_data_cBenef' label='Código de Benefício Fiscal' className='col-md-4 mb-4'>
              <Input
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_cBenef}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_cBenef}
                invalidFeedback={formik.errors.fiscal_data_cBenef}
              />
            </FormGroup>

            <FormGroup id='fiscal_data_cClassTrib' label='Classificação Tributária CBS/IBS' className='col-md-4 mb-4'>
              <input
                name='fiscal_data_cClassTrib'
                list='fiscal_data_cClassTrib-list'
                className={classNames('form-control', {
                  'is-invalid': !formik.isValid && formik.touched.fiscal_data_cClassTrib && formik.errors.fiscal_data_cClassTrib,
                  'is-valid': !formik.isValid && formik.touched.fiscal_data_cClassTrib && !formik.errors.fiscal_data_cClassTrib,
                })}
                value={formik.values.fiscal_data_cClassTrib}
                onChange={(e) => formik.setFieldValue('fiscal_data_cClassTrib', e.target.value.replace(/\D/g, ''))}
                onBlur={formik.handleBlur}
                placeholder='Digite 2+ dígitos'
              />
            </FormGroup>
            <datalist id='fiscal_data_cClassTrib-list'>
              {cClassTribDatalistOptions.map(item => (
                <option key={item.code} value={item.code} label={`${item.code} - ${item.text}`} />
              ))}
            </datalist>

            <FormGroup id='fiscal_data_cst_cbs_ibs' label='CST CBS/IBS' className='col-md-4 mb-4'>
              <Select
                list={cstCbsIbsOptions}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_cst_cbs_ibs}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_cst_cbs_ibs}
                invalidFeedback={formik.errors.fiscal_data_cst_cbs_ibs}
              />
            </FormGroup>

            <FormGroup id='fiscal_data_percentualReducao' label='Redução IBS/CBS' className='col-md-4 mb-4'>
              <Select
                list={percentualReducaoOptions}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_percentualReducao}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_percentualReducao}
                invalidFeedback={formik.errors.fiscal_data_percentualReducao}
              />
            </FormGroup>

            <FormGroup id='fiscal_data_pRedICMS' label='Redução da Base do ICMS (%)' className='col-md-4 mb-4'>
              <Input
                type='number'
                min='0'
                max='100'
                step='0.01'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fiscal_data_pRedICMS}
                isValid={formik.isValid}
                isTouched={formik.touched.fiscal_data_pRedICMS}
                invalidFeedback={formik.errors.fiscal_data_pRedICMS}
                placeholder='Ex.: 40'
              />
            </FormGroup>
          </div>

          <div className='row pt-4'>
            <div className='d-flex justify-content-end gap-2'>
              <Button
                color='danger'
                icon='cancel'
                rounded={1}
                onClick={setIsClose}
                shadow='sm'
                hoverShadow='sm'
                size='sm'>
                Cancelar
              </Button>
              {isLoading ? (
                <BtnLoad />
              ) : (
                <Button
                  isDisable={!formik.isValid && !!formik.submitCount}
                  type='submit'
                  color='success'
                  icon='check'
                  rounded={1}
                  hoverShadow='sm'
                  shadow='sm'
                  size='sm'>
                  Confirmar
                </Button>
              )}
            </div>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ModalRegisterFiscalGroups;
